import { Button, TextInput } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useRouter } from 'next/router';
import { DataTable } from '~/components/tables/common/DataTable';
import { DataTableHeader } from '~/components/tables/common/DataTableHeader';
import { useDataTable } from '~/components/tables/common/useDataTable';
import { tagColumns } from '~/components/tables/tag/tagColumns';
import { queryParamToString } from '~/logic/query-param';
import { api } from '~/trpc-extra/api';
import { useForm } from '@mantine/form';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useMemo } from 'react';
import { openDataTableDeleteModal } from '~/components/tables/common/DataTableDeleteModal';

export function useManageTagsDialog() {
  const openManageTagsDialog = () => {
    modals.open({
      size: 1000,
      zIndex: 310,
      title: (
        <div className="flex w-full items-center justify-between px-2">
          <div className="text-sm font-medium">Manage tags</div>
        </div>
      ),
      centered: true,
      children: <ManageTagsDialog />,
    });
  };

  return openManageTagsDialog;
}

function ManageTagsDialog() {
  const router = useRouter();
  const projectId = queryParamToString(router.query.projectId);
  const utils = api.useUtils();
  const { data: tags = [] } = api.tag.list.useQuery({ projectId });
  const { mutate: createTag } = api.tag.create.useMutation({
    onMutate: () => {
      modals.closeAll();
    },
    onSuccess: () => {
      notifications.show({
        title: 'Tag created',
        message: 'Tag created successfully',
      });
      utils.tag.list.invalidate().catch(() => undefined);
    },
    onError: (error) => {
      notifications.show({
        title: 'Error',
        message: error.message,
        color: 'red',
      });
    },
  });
  const { mutate: deleteTags } = api.tag.delete.useMutation({
    onSuccess: async () => {
      notifications.show({
        title: 'Tags deleted',
        message: 'Tags deleted successfully',
      });
      await Promise.allSettled([
        utils.tag.list.invalidate(),
        utils.test.listTests.invalidate({ projectId }),
        utils.test.getTest.invalidate(),
        utils.test.getTags.invalidate(),
      ]);
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Error deleting tags',
        color: 'red',
      });
    },
  });
  const table = useDataTable({
    columns: tagColumns,
    data: tags,
    enableNavigation: false,
  });

  const selectedRows = table.getFilteredSelectedRowModel().rows;

  const actions = useMemo(() => {
    return [
      {
        key: 'add-new',
        icon: IconPlus,
        label: 'New',
        color: 'blue',
        side: 'left' as const,
        onClick: () =>
          modals.open({
            title: 'Create tag',
            centered: true,
            zIndex: 320,
            children: <CreateTagModal onSubmit={(name) => createTag({ projectId, name })} />,
          }),
      },
      ...(selectedRows.length > 0 ?
        [
          {
            key: 'delete',
            icon: IconTrash,
            label: 'Delete',
            color: 'red',
            variant: 'light',
            side: 'right' as const,
            onClick: () => {
              modals.closeAll();
              openDataTableDeleteModal({
                count: selectedRows.length,
                itemName: 'tag',
                onConfirm: () => deleteTags({ tagIds: selectedRows.map((row) => row.original.id) }),
              });
            },
          },
        ]
      : []),
    ];
  }, [selectedRows]);

  return (
    <div className="flex h-full flex-col gap-2 px-8 py-4">
      <DataTableHeader
        actions={actions}
        table={table}
        itemName="tag"
      />
      <DataTable
        table={table}
        emptyMessage={<div className="py-8 text-start">No tags found</div>}
      />
    </div>
  );
}

function CreateTagModal({ onSubmit }: { onSubmit: (name: string) => void }) {
  const form = useForm({
    initialValues: {
      name: '',
    },
    validate: {
      name: (value) => (value.length < 1 ? 'Name is required' : null),
    },
  });
  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={form.onSubmit((values) => onSubmit(values.name))}
    >
      <TextInput
        autoFocus
        placeholder="Tag name"
        {...form.getInputProps('name')}
        error={form.errors.name}
      />
      <Button
        type="submit"
        disabled={!form.isValid()}
      >
        Create
      </Button>
    </form>
  );
}
